import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Circle, Play, Plus, Repeat, Send, Square, Trash2, X } from 'lucide-react';
import { meAPI, milestonesAPI, opsAPI, sharesAPI, tasksAPI, timeAPI } from '../lib/api';
import { getUser } from '../lib/auth';
import { t } from '../lib/i18n';
import { Avatar, LabelChip, PRIORITY_META, btnGhost, fmtDateTime, inputCls } from './ui';

// h:mm:ss (or mm:ss under an hour) for the task time tally
function fmtDuration(total) {
  const s = Math.max(0, Math.floor(total || 0));
  const pad = (n) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

// `standalone` = opened from My Work / a notification by someone who may not be a
// project member (an assigned operator/contributor). It fetches its own minimal
// context from /me/tasks/{id} and hides every project-scoped control (board
// navigation, milestone, blockers, label creation, timer) — the person can read
// and work THEIR task, nothing else.
export default function TaskDetailPanel({
  taskId,
  project: projectProp,
  roster: rosterProp = [],
  labels: labelsProp = [],
  readOnly,
  standalone = false,
  onClose,
  onChanged,
  onLabelsChanged,
}) {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [taskTime, setTaskTime] = useState({ total_seconds: 0, running: null });
  const [nowTick, setNowTick] = useState(Date.now());
  // in standalone mode the project/roster/labels come from /me/tasks/{id}
  const [ctx, setCtx] = useState(null);
  const user = getUser();
  const dirty = useRef(false);

  const project = standalone ? ctx?.project : projectProp;
  const roster = standalone ? ctx?.roster || [] : rosterProp;
  const labels = standalone ? ctx?.labels || [] : labelsProp;

  const load = useCallback(async () => {
    const [t, c] = await Promise.all([tasksAPI.get(taskId), tasksAPI.comments(taskId)]);
    setTask(t.data);
    setTitle(t.data.title);
    setDescription(t.data.description_rt || '');
    setComments(c.data.comments);
  }, [taskId]);

  useEffect(() => {
    if (!standalone) return undefined;
    let alive = true;
    meAPI
      .taskDetail(taskId)
      .then(({ data }) => {
        if (alive) setCtx({ project: data.project, roster: data.assignees, labels: data.labels });
      })
      .catch(() => alive && onClose());
    return () => {
      alive = false;
    };
  }, [standalone, taskId, onClose]);

  useEffect(() => {
    // project-scoped context lists — only when we have full project access
    if (standalone || !project) return;
    milestonesAPI.list(project.id).then(({ data }) => setMilestones(data.milestones));
    tasksAPI.customFields(project.id).then(({ data }) => setCustomFields(data.fields));
    tasksAPI.list(project.id).then(({ data }) => setProjectTasks(data.tasks));
    tasksAPI.groups(project.id).then(({ data }) => setGroups(data.groups));
  }, [standalone, project, taskId]);

  const loadTaskTime = useCallback(async () => {
    try {
      const { data } = await timeAPI.taskTime(taskId);
      setTaskTime(data);
    } catch {
      /* no access yet / nothing tracked */
    }
  }, [taskId]);

  const toggleTimer = async () => {
    if (taskTime.running) await timeAPI.stop(taskId);
    else await timeAPI.start(taskId);
    window.dispatchEvent(new Event('pms-timer-changed'));
    await loadTaskTime();
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadTaskTime();
  }, [loadTaskTime]);

  // live 1s tick only while a timer is running on this task
  useEffect(() => {
    if (!taskTime.running) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [taskTime.running]);

  const patch = async (fields) => {
    await tasksAPI.patch(taskId, fields);
    dirty.current = true;
    await load();
  };

  const close = () => {
    if (dirty.current) onChanged();
    onClose();
  };

  const saveTitle = async () => {
    if (title.trim() && title.trim() !== task.title) await patch({ title: title.trim() });
  };

  const saveDescription = async () => {
    if (description !== (task.description_rt || '')) await patch({ description_rt: description });
  };

  // move the task to another list/status group (appends to the end of it);
  // the backend completes/reopens automatically for a done column
  const moveToGroup = async (gid) => {
    if (!gid || gid === task.group_id) return;
    await tasksAPI.move(taskId, gid, Date.now());
    dirty.current = true;
    await load();
  };

  // create a milestone right from the task (so the milestone manager is reachable
  // where you look for it, not only on the Roadmap tab) and assign this task to it
  const createMilestone = async () => {
    const name = window.prompt(t('New milestone name'));
    if (!name || !name.trim()) return;
    const { data } = await milestonesAPI.create(project.id, { name: name.trim() });
    setMilestones((prev) => [...prev, data]);
    await patch({ milestone_id: data.id });
  };

  const setAssignees = (mids) => patch({ assignee_mids: Array.from(new Set(mids)) });

  const toggleLabel = (labelId) => {
    const cur = task.labels || [];
    patch({ labels: cur.includes(labelId) ? cur.filter((x) => x !== labelId) : [...cur, labelId] });
  };

  const createLabel = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    const { data } = await tasksAPI.createLabel(project.id, { name: newLabel.trim() });
    setNewLabel('');
    await onLabelsChanged();
    await patch({ labels: [...(task.labels || []), data.id] });
  };

  const addSubtask = async (e) => {
    e.preventDefault();
    if (!subtaskTitle.trim()) return;
    await patch({ subtasks: [...(task.subtasks || []), { title: subtaskTitle.trim(), done: false }] });
    setSubtaskTitle('');
  };

  const toggleSubtask = (id) =>
    patch({ subtasks: (task.subtasks || []).map((s) => (s.id === id ? { ...s, done: !s.done } : s)) });

  const removeSubtask = (id) => patch({ subtasks: (task.subtasks || []).filter((s) => s.id !== id) });

  const toggleComplete = async () => {
    if (task.completed_at) await tasksAPI.reopen(taskId);
    else await tasksAPI.complete(taskId);
    dirty.current = true;
    await load();
  };

  const removeTask = async () => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    await tasksAPI.remove(taskId);
    onChanged();
    onClose();
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await tasksAPI.addComment(taskId, { body_rt: comment.trim() });
    setComment('');
    const { data } = await tasksAPI.comments(taskId);
    setComments(data.comments);
  };

  const deleteComment = async (id) => {
    await tasksAPI.deleteComment(id);
    setComments((prev) => prev.filter((c) => c.id !== id));
  };

  if (!task || !project) return null;
  const done = Boolean(task.completed_at);
  const runningSecs = taskTime.running
    ? Math.max(0, Math.floor((nowTick - Date.parse(taskTime.running.started_at)) / 1000))
    : 0;
  const trackedSecs = (taskTime.total_seconds || 0) + runningSecs;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="pm-drawer-in flex h-full w-full max-w-lg flex-col border-l border-pm-border bg-pm-surface shadow-[var(--pm-elev-3)]">
        <div className="flex items-center gap-2 border-b border-pm-border px-4 py-3">
          {!readOnly && (
            <button
              type="button"
              onClick={toggleComplete}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                done ? 'border-pm-accent text-pm-accent' : 'border-pm-border text-pm-muted hover:text-pm-text'
              }`}
            >
              {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
              {done ? 'Completed' : 'Mark complete'}
            </button>
          )}
          {!readOnly && (
            <div
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                taskTime.running ? 'border-pm-accent text-pm-accent' : 'border-pm-border text-pm-muted'
              }`}
              title={t('Time tracked')}
            >
              <span className="pm-num">{fmtDuration(trackedSecs)}</span>
              <button
                type="button"
                onClick={toggleTimer}
                className="hover:text-pm-text"
                title={taskTime.running ? t('Stop timer') : t('Start timer')}
              >
                {taskTime.running ? <Square size={12} fill="currentColor" /> : <Play size={12} />}
              </button>
            </div>
          )}
          <span className="ml-auto" />
          {!readOnly && !standalone && (
            <button type="button" onClick={removeTask} className="rounded p-1.5 text-pm-faint hover:text-pm-danger" title="Delete task" style={{ color: undefined }}>
              <Trash2 size={15} />
            </button>
          )}
          <button type="button" onClick={close} className="rounded p-1.5 text-pm-muted hover:bg-pm-raised hover:text-pm-text">
            <X size={16} />
            <span className="sr-only">Close</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
            disabled={readOnly}
            className={`w-full bg-transparent text-base font-semibold outline-none ${done ? 'line-through opacity-60' : ''}`}
          />

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <span className="mb-1 block text-xs text-pm-faint">Start date</span>
              <input
                type="date"
                value={task.start_date || ''}
                onChange={(e) => patch({ start_date: e.target.value || null, clear_dates: !e.target.value })}
                disabled={readOnly}
                className={`${inputCls} py-1.5 text-xs`}
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-pm-faint">Due date</span>
              <input
                type="date"
                value={task.due_date || ''}
                onChange={(e) => patch({ due_date: e.target.value || null, clear_dates: !e.target.value })}
                disabled={readOnly}
                className={`${inputCls} py-1.5 text-xs`}
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-pm-faint">Priority</span>
              <select
                value={task.priority || 'none'}
                onChange={(e) => patch({ priority: e.target.value })}
                disabled={readOnly}
                className={`${inputCls} py-1.5 text-xs`}
              >
                {Object.entries(PRIORITY_META).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            {!standalone && groups.length > 0 && (
              <div>
                <span className="mb-1 block text-xs text-pm-faint">{t('Status')}</span>
                <select
                  value={task.group_id}
                  onChange={(e) => moveToGroup(e.target.value)}
                  disabled={readOnly}
                  className={`${inputCls} py-1.5 text-xs`}
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-4">
            <span className="mb-1 block text-xs text-pm-faint">{t('Assignees')}</span>
            {standalone ? (
              <div className="flex flex-wrap gap-1.5">
                {roster.length === 0 && <span className="text-xs text-pm-faint">{t('Unassigned')}</span>}
                {roster.map((m) => (
                  <span
                    key={m.mid}
                    title={m.name}
                    className="inline-flex items-center gap-1.5 rounded-full border border-pm-accent px-2 py-0.5 text-xs"
                  >
                    <Avatar name={m.name} size={18} />
                    <span className="max-w-[9rem] truncate">{m.name}</span>
                  </span>
                ))}
              </div>
            ) : (
              <AssigneePicker
                roster={roster}
                selected={task.assignee_mids || []}
                disabled={readOnly}
                onChange={setAssignees}
              />
            )}
          </div>

          {!standalone && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <span className="mb-1 block text-xs text-pm-faint">Milestone</span>
              <select
                value={task.milestone_id || ''}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    createMilestone();
                    return;
                  }
                  patch(e.target.value ? { milestone_id: e.target.value } : { milestone_id: null, clear_milestone: true });
                }}
                disabled={readOnly}
                className={`${inputCls} py-1.5 text-xs`}
              >
                <option value="">{t('No milestone')}</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
                {!readOnly && <option value="__new__">+ {t('New milestone')}…</option>}
              </select>
            </div>
            <div>
              <span className="mb-1 flex items-center gap-1 text-xs text-pm-faint">
                <Repeat size={11} /> Repeats
              </span>
              <div className="flex gap-1.5">
                <select
                  value={task.recurrence?.freq || ''}
                  onChange={(e) => {
                    const freq = e.target.value;
                    if (!freq) patch({ recurrence: null, clear_recurrence: true });
                    else
                      patch({
                        recurrence: {
                          freq,
                          interval: task.recurrence?.interval || 1,
                          next_at: task.recurrence?.next_at || `${task.due_date || new Date().toISOString().slice(0, 10)}T00:00:00+00:00`,
                        },
                      });
                  }}
                  disabled={readOnly}
                  className={`${inputCls} py-1.5 text-xs`}
                >
                  <option value="">Never</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                {task.recurrence?.freq && (
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={task.recurrence?.interval || 1}
                    onChange={(e) => patch({ recurrence: { ...task.recurrence, interval: Number(e.target.value) || 1 } })}
                    disabled={readOnly}
                    title="Every N periods"
                    className={`${inputCls} w-16 py-1.5 text-xs`}
                  />
                )}
              </div>
            </div>
          </div>
          )}

          {!standalone && (
          <div className="mt-4">
            <span className="mb-1 block text-xs text-pm-faint">Blocked by</span>
            <div className="flex flex-wrap gap-1.5">
              {((task.dependencies || {}).blocked_by || []).map((bid) => {
                const b = projectTasks.find((t) => t.id === bid);
                return (
                  <span key={bid} className="inline-flex items-center gap-1 rounded-full bg-pm-raised px-2 py-0.5 text-xs">
                    {b?.title || 'Unknown task'}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() =>
                          patch({ blocked_by: ((task.dependencies || {}).blocked_by || []).filter((x) => x !== bid) })
                        }
                        className="text-pm-faint hover:text-pm-danger"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </span>
                );
              })}
              {!readOnly && (
                <select
                  value=""
                  onChange={(e) =>
                    e.target.value &&
                    patch({ blocked_by: [...((task.dependencies || {}).blocked_by || []), e.target.value] })
                  }
                  className={`${inputCls} w-auto py-0.5 text-xs`}
                >
                  <option value="">+ add blocker…</option>
                  {projectTasks
                    .filter((t) => t.id !== taskId && !((task.dependencies || {}).blocked_by || []).includes(t.id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                </select>
              )}
            </div>
          </div>
          )}

          {customFields.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {customFields.map((f) => {
                const val = (task.custom_fields || {})[f.id] ?? '';
                const save = (v) => patch({ custom_fields: { ...(task.custom_fields || {}), [f.id]: v } });
                return (
                  <div key={f.id}>
                    <span className="mb-1 block text-xs text-pm-faint">{f.name}</span>
                    {f.type === 'select' ? (
                      <select value={val} onChange={(e) => save(e.target.value)} disabled={readOnly} className={`${inputCls} py-1.5 text-xs`}>
                        <option value="">—</option>
                        {(f.options || []).map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={f.type === 'number' || f.type === 'currency' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                        defaultValue={val}
                        onBlur={(e) => e.target.value !== String(val) && save(e.target.value)}
                        disabled={readOnly}
                        className={`${inputCls} py-1.5 text-xs`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4">
            <span className="mb-1 block text-xs text-pm-faint">Labels</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {labels.map((l) => {
                const on = (task.labels || []).includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    disabled={readOnly || standalone}
                    onClick={() => toggleLabel(l.id)}
                    className={on ? '' : 'opacity-40 hover:opacity-100'}
                  >
                    <LabelChip label={l} />
                  </button>
                );
              })}
              {!readOnly && !standalone && (
                <form onSubmit={createLabel}>
                  <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="+ new label"
                    className="w-24 rounded-full border border-dashed border-pm-border bg-transparent px-2 py-0.5 text-xs outline-none focus:border-[var(--pm-focus-border)]"
                  />
                </form>
              )}
            </div>
          </div>

          <div className="mt-4">
            <span className="mb-1 block text-xs text-pm-faint">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={saveDescription}
              disabled={readOnly}
              rows={4}
              placeholder="Add a description…"
              className={inputCls}
            />
          </div>

          {/* duty verification hint (D8): on Morning-Brief duty tasks, operators see
              the latest published brief so a silent automation failure is caught
              before checking off. Title-based match by convention. */}
          {/morning\s*brief/i.test(task.title || '') && ['admin', 'operator'].includes(user?.pr) && (
            <MorningBriefHint />
          )}

          {/* share task (Phase 4): the assignee's network buttons, or the
              operator's attach editor when no item is set yet */}
          {task.share_item ? (
            <SharePanel task={task} user={user} onChanged={load} />
          ) : (
            ['admin', 'operator'].includes(user?.pr) && !readOnly && (
              <ShareItemAttach taskId={taskId} onSet={load} />
            )
          )}

          <div className="mt-4">
            <span className="mb-1 block text-xs text-pm-faint">
              Subtasks{' '}
              {(task.subtasks || []).length > 0 &&
                `· ${(task.subtasks || []).filter((s) => s.done).length}/${(task.subtasks || []).length}`}
            </span>
            <ul className="space-y-1">
              {(task.subtasks || []).map((s) => (
                <li key={s.id} className="group flex items-center gap-2 rounded px-1 py-0.5 hover:bg-pm-raised">
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => toggleSubtask(s.id)}
                    className={s.done ? 'text-pm-accent' : 'text-pm-faint hover:text-pm-accent'}
                  >
                    {s.done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                  </button>
                  <span className={`flex-1 text-sm ${s.done ? 'line-through opacity-60' : ''}`}>{s.title}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeSubtask(s.id)}
                      className="text-pm-faint opacity-0 hover:text-pm-danger group-hover:opacity-100"
                    >
                      <X size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {!readOnly && (
              <form onSubmit={addSubtask} className="mt-1.5 flex items-center gap-1.5">
                <Plus size={13} className="text-pm-faint" />
                <input
                  value={subtaskTitle}
                  onChange={(e) => setSubtaskTitle(e.target.value)}
                  placeholder="Add subtask"
                  className="flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-pm-faint"
                />
              </form>
            )}
          </div>

          <div className="mt-6 border-t border-pm-border pt-4">
            <span className="mb-2 block text-xs text-pm-faint">Comments</span>
            <ul className="space-y-3">
              {comments.map((c) => (
                <li key={c.id} className="group">
                  <div className="flex items-center gap-2">
                    <Avatar name={c.author_name} size={20} />
                    <span className="text-xs font-medium">{c.author_name}</span>
                    <span className="text-[10px] text-pm-faint">{fmtDateTime(c.created_at)}</span>
                    {(c.author_mid === (user?.mid || '') || user?.role === 'admin') && (
                      <button
                        type="button"
                        onClick={() => deleteComment(c.id)}
                        className="ml-auto text-pm-faint opacity-0 hover:text-pm-danger group-hover:opacity-100"
                        title="Delete comment"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <p className="ml-7 mt-0.5 whitespace-pre-wrap text-sm text-pm-text">{c.body_rt}</p>
                </li>
              ))}
              {comments.length === 0 && <li className="text-xs text-pm-faint">No comments yet.</li>}
            </ul>
            <form onSubmit={sendComment} className="mt-3 flex gap-2">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write a comment…"
                className={`${inputCls} py-1.5`}
              />
              <button type="submit" disabled={!comment.trim()} className={`${btnGhost} px-2.5`} title="Send">
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Assignment picker — a type-to-filter people selector. The admin/operator
// searches by name (or hits "Show all"), and can bulk-assign a whole role in one
// click ("All Contributors" for a share-to-earn task that everyone should get,
// "All Operators" likewise). Each pick fires the "assigned" notification, which
// is what lets that operator/contributor open the task from My Work.
const ROLE_RANK = { admin: 0, operator: 1, contributor: 2, member: 3 };
const ROLE_LABEL = { admin: 'admin', operator: 'operator', contributor: 'contributor', member: 'member' };

function AssigneePicker({ roster, selected, disabled, onChange }) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const sel = new Set(selected);
  const q = query.trim().toLowerCase();
  const midsOfRole = (role) => roster.filter((m) => m.pms_role === role).map((m) => m.mid);
  const counts = {
    operator: roster.filter((m) => m.pms_role === 'operator').length,
    contributor: roster.filter((m) => m.pms_role === 'contributor').length,
  };

  const toggle = (mid) => onChange(sel.has(mid) ? selected.filter((x) => x !== mid) : [...selected, mid]);
  const assignRole = (role) => onChange([...selected, ...midsOfRole(role)]);

  // what to show: search matches when typing; else the current picks, unless "Show all"
  const sorted = [...roster].sort(
    (a, b) => (ROLE_RANK[a.pms_role] ?? 9) - (ROLE_RANK[b.pms_role] ?? 9) || a.name.localeCompare(b.name),
  );
  const visible = q
    ? sorted.filter((m) => m.name.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    : showAll
      ? sorted
      : sorted.filter((m) => sel.has(m.mid));

  return (
    <div>
      {!disabled && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search people…')}
            className={`${inputCls} h-8 flex-1 py-1 text-xs`}
          />
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className={`rounded-md border px-2 py-1 text-[11px] ${showAll ? 'border-pm-accent text-pm-accent' : 'border-pm-border text-pm-muted hover:text-pm-text'}`}
          >
            {t('Show all')}
          </button>
        </div>
      )}
      {!disabled && (counts.operator > 0 || counts.contributor > 0) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {counts.operator > 0 && (
            <button type="button" onClick={() => assignRole('operator')} className="rounded-full border border-pm-border px-2 py-0.5 text-[11px] text-pm-muted hover:text-pm-text">
              + {t('All Operators')} ({counts.operator})
            </button>
          )}
          {counts.contributor > 0 && (
            <button type="button" onClick={() => assignRole('contributor')} className="rounded-full border border-pm-border px-2 py-0.5 text-[11px] text-pm-muted hover:text-pm-text">
              + {t('All Contributors')} ({counts.contributor})
            </button>
          )}
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="rounded-full px-2 py-0.5 text-[11px] text-pm-faint hover:text-pm-danger">
              {t('Clear')}
            </button>
          )}
        </div>
      )}
      {visible.length === 0 ? (
        <span className="text-xs text-pm-faint">
          {q ? t('No matches') : selected.length === 0 ? t('No one assigned — search or Show all') : ''}
        </span>
      ) : (
        <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
          {visible.map((m) => {
            const on = sel.has(m.mid);
            return (
              <button
                key={m.mid}
                type="button"
                disabled={disabled}
                onClick={() => toggle(m.mid)}
                title={`${m.name} · ${t(ROLE_LABEL[m.pms_role] || 'member')}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${
                  on ? 'border-pm-accent text-pm-text' : 'border-pm-border text-pm-muted opacity-80 hover:opacity-100'
                }`}
              >
                <Avatar name={m.name} size={18} />
                <span className="max-w-[9rem] truncate">{m.name}</span>
                {on && <CheckCircle2 size={12} className="text-pm-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// D8 verification hint: the latest published Morning Brief, read from the
// brand's morning_pages by the PMS backend. Rendered only on operator/admin
// sessions inside Morning-Brief duty tasks.
function MorningBriefHint() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    opsAPI.morningBriefStatus().then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  if (!status) return null;
  return (
    <div className="mt-4 rounded-md border border-pm-border bg-pm-raised px-3 py-2 text-xs">
      <span className="text-pm-faint">{t('Last brief published')}: </span>
      {status.published ? (
        <>
          <a
            href={status.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pm-accent underline"
          >
            {status.title || status.slug}
          </a>
          {status.created_at && (
            <span className="ml-1 text-pm-faint">· {fmtDateTime(status.created_at)}</span>
          )}
        </>
      ) : (
        <span className="text-pm-muted">{t('none yet')}</span>
      )}
    </div>
  );
}

// Phase 4 — share task panel: one button per allowed network. The click is the
// tracked delivery event (records pm_shares + returns the intent URL, opened in
// a new tab under the contributor's OWN account). Done networks show a check.
const NETWORK_LABELS = { x: 'X (Twitter)', facebook: 'Facebook', linkedin: 'LinkedIn', whatsapp: 'WhatsApp' };

function SharePanel({ task, user, onChanged }) {
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState('');
  const item = task.share_item;
  const isOp = ['admin', 'operator'].includes(user?.pr);
  const loadShares = useCallback(() => {
    sharesAPI.taskShares(task.id).then(({ data }) => setShares(data.shares)).catch(() => {});
  }, [task.id]);
  useEffect(() => { loadShares(); }, [loadShares]);

  const mine = new Set(shares.filter((s) => s.author_mid === (user?.mid || '')).map((s) => s.network));
  const click = async (network) => {
    setBusy(network);
    try {
      const { data } = await sharesAPI.click(task.id, network);
      window.open(data.intent_url, '_blank', 'noopener,noreferrer');
      loadShares();
    } catch {
      /* not the assignee / network disabled — button state already reflects it */
    } finally {
      setBusy('');
    }
  };
  const clearItem = async () => {
    await sharesAPI.setShareItem(task.id, { clear: true });
    onChanged();
  };

  return (
    <div className="mt-4 rounded-md border border-pm-border bg-pm-raised p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{t('Share this content')}</span>
        {isOp && (
          <button type="button" onClick={clearItem} className="text-[10px] text-pm-faint hover:text-pm-danger">
            {t('Remove')}
          </button>
        )}
      </div>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-1 block truncate text-sm text-pm-accent underline">
        {item.title}
      </a>
      {item.message && <p className="mt-1 text-xs text-pm-muted">"{item.message}"</p>}
      {item.scheduled_at && (
        <p className="mt-1 text-[10px] text-pm-faint">
          {t('Scheduled for')} {fmtDateTime(item.scheduled_at)}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {(item.networks || []).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => click(n)}
            disabled={busy === n}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
              mine.has(n)
                ? 'border-pm-accent text-pm-accent'
                : 'border-pm-border text-pm-muted hover:text-pm-text'
            }`}
          >
            {mine.has(n) ? <CheckCircle2 size={13} /> : <Send size={12} />}
            {t('Share on')} {NETWORK_LABELS[n] || n}
          </button>
        ))}
      </div>
      {shares.length > 0 && (
        <p className="mt-2 text-[10px] text-pm-faint">
          {shares.length} {t('share(s) recorded')}
        </p>
      )}
    </div>
  );
}

// Phase 4 — operator attach editor: search the brand's content, pick networks,
// optional suggested message / schedule / attribution override.
function ShareItemAttach({ taskId, onSet }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [picked, setPicked] = useState(null);
  const [message, setMessage] = useState('');
  const [networks, setNetworks] = useState(Object.keys(NETWORK_LABELS));
  const [attribution, setAttribution] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return undefined; }
    const h = setTimeout(() => {
      sharesAPI.contentSearch(q.trim()).then(({ data }) => setFound(data.items)).catch(() => {});
    }, 300);
    return () => clearTimeout(h);
  }, [q]);

  const toggleNetwork = (n) =>
    setNetworks((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));

  const save = async () => {
    await sharesAPI.setShareItem(taskId, {
      ...picked,
      message: message.trim(),
      networks,
      attribution: attribution || null,
      scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    });
    onSet();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-dashed border-pm-border px-2.5 py-1.5 text-xs text-pm-muted hover:text-pm-text"
      >
        <Send size={12} />
        {t('Attach content to share')}
      </button>
    );
  }
  return (
    <div className="mt-4 rounded-md border border-pm-border bg-pm-raised p-3">
      <span className="text-xs font-semibold">{t('Attach content to share')}</span>
      {!picked ? (
        <>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Search articles, news, briefs…')}
            className={`${inputCls} mt-2 py-1.5 text-xs`}
            autoFocus
          />
          <ul className="mt-1 max-h-40 overflow-y-auto">
            {found.map((it) => (
              <li key={`${it.product}-${it.item_id}`}>
                <button
                  type="button"
                  onClick={() => setPicked(it)}
                  className="w-full rounded px-2 py-1.5 text-left text-xs hover:bg-pm-surface"
                >
                  <span className="mr-1.5 rounded bg-pm-surface px-1 py-0.5 text-[9px] uppercase text-pm-faint">{it.product}</span>
                  {it.title}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <p className="mt-2 truncate text-xs text-pm-accent">{picked.title}</p>
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('Suggested message (optional)')}
            className={`${inputCls} mt-2 py-1.5 text-xs`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(NETWORK_LABELS).map(([n, label]) => (
              <label key={n} className="inline-flex items-center gap-1 text-xs text-pm-muted">
                <input type="checkbox" checked={networks.includes(n)} onChange={() => toggleNetwork(n)} />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <span className="mb-0.5 block text-[10px] text-pm-faint">{t('Attribution')}</span>
              <select value={attribution} onChange={(e) => setAttribution(e.target.value)} className={`${inputCls} py-1.5 text-xs`}>
                <option value="">{t('Member default')}</option>
                <option value="name_ref">{t('With referral code')}</option>
                <option value="name_only">{t('Name only (no code)')}</option>
              </select>
            </div>
            <div>
              <span className="mb-0.5 block text-[10px] text-pm-faint">{t('Schedule (optional)')}</span>
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={`${inputCls} py-1.5 text-xs`} />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={save} disabled={networks.length === 0} className="rounded-md bg-pm-accent px-2.5 py-1.5 text-xs font-semibold text-pm-accent-ink disabled:opacity-60">
              {t('Save')}
            </button>
            <button type="button" onClick={() => setPicked(null)} className={btnGhost}>
              {t('Back')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
